/* Хвост BotRun шаблона, который ведёт сервер (engine-kit, план движков §5.5): подвал отказов и тексты итога только из заказа.
   BotRun (src/legacy/bot-run.js с ручными правками drive/adopt/advance/finish/receipt/failText) рисует страницу заказа сам, а суммы
   берёт отсюда: чек и подпись ошибки — из order.financial, charge и balance (tailReceipt, tailFailText), никогда не r.price и не ui.balance() (I3).
   Свои итоги сервера: rej — заказ не создан (отказ до создания, «К настройкам»), unk — исход не подтверждён («Проверить ещё раз»). */
import type { SpeechOrder } from '../../../api/speech';
import { uiFormat, uiText } from '../../../i18n';
import { cNum, tailFailText, tailReceipt, type TailStep } from '../circle/order-tail-model';

/** Заказ BotRun (RUN): st — run | done | fail; err у fail — mod | tech шаблона или rej | unk сервера */
export interface BotRunRun {
  st: 'run' | 'done' | 'fail';
  err: null | 'mod' | 'tech' | 'unk' | 'rej';
  step: number;
  steps: string[][];
  id: number;
  /** Цена при создании хвоста: на странице не показывается (I3) */
  price: number;
  snap: Record<string, unknown> & { cheap?: boolean };
  sent?: Record<string, boolean>;
  ms: number;
  t1: number;
  k?: number;
}
/** BotRun.create → хвост заказа (с ручными правками src/legacy) */
export interface BotRunApi {
  readonly run: BotRunRun | null;
  start(): void;
  retry(): void;
  exit(form: () => void): void;
  drop(): void;
  sync(): void;
  show(host: HTMLElement): boolean;
  refresh(): void;
  adopt(fields: Partial<BotRunRun>): BotRunRun;
  advance(step: number, k?: number): void;
  finish(st: 'done' | 'fail', err: BotRunRun['err']): void;
}

/** Экранирование текста для разметки BotRun (тексты сервера и формы) */
export const esc = (value: string) =>
  value.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);

/** Подвал хвоста сервера: tech — «Попробовать ещё раз», unk — «Проверить ещё раз», rej — «К настройкам». Атрибуты с приставкой экрана
    обрабатывает SV (click), поэтому они заменяют свои действия шаблона (data-run-retry BotRun на сбое не ставится).
    Подписи — функции (BotRun берёт их через val при каждом показе): хвост живёт дольше языка профиля, смена языка его не пересоздаёт. */
export function serverFoot(base: Record<string, unknown>, p: string): Record<string, unknown> {
  return {
    ...base,
    tech: [`data-${p}-retry`, 'refresh', () => uiText('Попробовать ещё раз')],
    unk: [`data-${p}-rchk`, 'refresh', () => uiText('Проверить ещё раз')],
    rej: [`data-${p}-fix`, 'back', () => uiText('К настройкам')],
  };
}

const KEPT = 'Заказ не подтверждён. Продолжение проверит прежний запрос с тем же ключом.';

/** Заголовок и подпись итога с ошибкой ('<h2>…</h2><p>…</p>'): суммы только из заказа; null — заказ не на ошибке.
    rej — «Заказ не создан» и причина отказа сервиса (svc.error или integrityError); unk без заказа — «Заказ не подтверждён» и причина
    (по умолчанию — прежний запрос с тем же ключом); unk с заказом (uncertain) — «Уточняем результат»; mod и tech — тексты экрана и tailFailText. */
export function failText(
  r: BotRunRun,
  x: {
    order: SpeechOrder | null;
    error: string;
    texts: { modH: string; techH: string; notMade: string; modWhy?: string; techWhy?: string };
    step?: TailStep;
  },
): string | null {
  if (r.st !== 'fail') return null;
  const page = (heading: string, text: string) => `<h2>${esc(uiText(heading))}</h2><p>${esc(text)}</p>`;
  if (r.err === 'rej') return page('Заказ не создан', uiText(x.error || 'Проверьте заполненные поля. Заказ не создан.'));
  if (r.err === 'unk')
    return x.order
      ? page('Уточняем результат', uiText('Результат пока не подтверждён. Проверьте этот заказ, прежде чем создавать новый.'))
      : page('Заказ не подтверждён', uiText(x.error || KEPT));
  const phase = r.err === 'mod' ? 'mod' : 'tech';
  const texts = { notMade: x.texts.notMade, modWhy: x.texts.modWhy, techWhy: x.texts.techWhy };
  return page(phase === 'mod' ? x.texts.modH : x.texts.techH, tailFailText(phase, x.order, texts, phase === 'tech' ? x.step : undefined));
}

/** Чек под итогом ('<p class="ar-rc">…</p>'), как rc() BotRun, но из заказа: списано или вернули (со скидкой — с водяным знаком,
    только у готового заказа со скидкой), остаток — только если сервер его прислал; dev_free — «Тестовый заказ без списания».
    '' — у rej и unk и без чека сервера. */
export function receipt(r: BotRunRun, order: SpeechOrder | null, o: { cheap: boolean; where: string }): string {
  if (r.err === 'rej' || r.err === 'unk') return '';
  const value = tailReceipt(order);
  if (!value) return '';
  if (value.kind === 'free') return `<p class="ar-rc">${esc(uiText('Тестовый заказ без списания'))}</p>`;
  let html = esc(uiText(value.kind === 'refund' ? 'Вернули' : 'Списано'));
  if (value.amount !== null) html += ` <b class="mono">${cNum(value.amount)} C</b>`;
  if (value.kind === 'debit' && o.cheap && r.st === 'done') html += ' ' + esc(uiFormat('со скидкой, {0} водяной знак', uiText(o.where)));
  if (value.balance !== null) html += ` · ${esc(uiText('остаток'))} <b class="mono">${cNum(value.balance)} C</b>`;
  return `<p class="ar-rc">${html}</p>`;
}
